import React, { useState } from 'react';
import { styled } from '@ff/ui';
import Box from '@ff/ui/Box';
import { colors, H1, H3, P12, P14, P16 } from 'client/v3/typography';
import Currency from 'client/v3/components/Currency';
import PriceSummary from 'client/v3/components/PriceSummary';
import Button from '@ff/ui/Button';
import Text from '@ff/ui/Text';
import FlexCol from '@ff/ui/FlexCol';
import LeaseOptionModal from './LeaseOptionModal';
import PaymentOptions, { PaymentMethod } from './PaymentOptions';
import { LEASE_OPTION } from 'client/settings';
import FlexRow from '@ff/ui/FlexRow';
import { destinationFeeFormatted, annualMiles, useLeaseContext, termsFormatted, defaultDownPayment } from './LeaseContext';

const hasLeaseOption = !(typeof LEASE_OPTION === 'undefined' || LEASE_OPTION === 'DISABLED');

export default function SummaryDetail({ summary }) {
  const {
    basePrice,
    vehicleOptions,
    subtotal,
    preorderPaidAmount,
    preorderBalance,
    preorderDepositAmount,
    dueAmount,
    vehicleModel
  } = summary || {};

  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('cash');
  const [showMoreLeaseOptionModal, setShowMoreLeaseOptionModal] = useState(false);

  const options = vehicleOptions?.map(op => ({
    categoryName: op.vehicleOptionType.categoryName,
    code: op.vehicleOptionType.typeCode,
    name: op.vehicleOptionType.typeName,
    price: op.price,
  })) || [];

  const isAlliance = vehicleModel?.fullModelCode === 'FF91FNA-ALLIANCE';

  const priceSummaryProps = {
    modelCode: vehicleModel?.fullModelCode,
    basePrice,
    options: [
      {
        ...options.find(x => x.categoryName === 'Exterior'),
      },
      {
        ...options.find(x => x.categoryName === 'Wheel'),
        categoryName: 'Wheels',
      },
      {
        ...options.find(x => x.categoryName === 'Interior'),
      },
    ],
  }

  return (
    <>
      <H1 textAlign='center'>{vehicleModel?.name}</H1>
      <Row my='30px'>
        <H3>Your Summary:</H3>
      </Row>
      <Box mb='20px'>
        <PriceSummary {...priceSummaryProps}></PriceSummary>
      </Box>
      <Divider />
      {/* new feature */}
      {
        hasLeaseOption &&
        <>
          <PaymentOptions paymentMethod={paymentMethod} onPaymentMethodChange={(value: PaymentMethod) => setPaymentMethod(value)} />
          {paymentMethod === 'cash'
            ? <CashPrice subtotal={subtotal} />
            : <LeasePrice isAlliance={isAlliance} onMoreClick={() => setShowMoreLeaseOptionModal(true)} />
          }
        </>
      }
      {/* without new feature */}
      {
        !hasLeaseOption &&
        <CashPrice subtotal={subtotal} />
      }

      <Row mt='58px' mb='32px'>
        <P14>Deposit amount</P14>
        <P16><Currency>{preorderDepositAmount}</Currency></P16>
      </Row>
      {preorderPaidAmount > 0 &&
        <Row my='32px'>
          <P16>Paid amount</P16>
          <P16>-<Currency>{preorderPaidAmount}</Currency></P16>
        </Row>
      }
      {preorderBalance > 0 &&
        <Row my='32px'>
          <P16>Credit</P16>
          <P16><Currency>{preorderBalance}</Currency></P16>
        </Row>
      }
      <Divider2 />
      <Row mt='32px' mb='16px'>
        <P16 fontWeight={450}>Due Today</P16>
        <P16 fontWeight={450}><Currency>{dueAmount}</Currency></P16>
      </Row>
      <P12 mb='139px' color={colors.content1} >Full refund is available before order confirmation</P12>
      <LeaseOptionModal vehiclePrice={basePrice} open={showMoreLeaseOptionModal} onClose={() => setShowMoreLeaseOptionModal(false)} />
    </>
  );
}

function LeasePrice({ isAlliance, onMoreClick }) {
  const { leaseOptions } = useLeaseContext();

  let showLease = false;
  let showComingSoon = false;

  if (LEASE_OPTION === 'ENABLED' && isAlliance) {
    showLease = true;
  }

  if (LEASE_OPTION === 'COMING_SOON'
    // not open for futurist yet
    || (LEASE_OPTION === 'ENABLED' && !isAlliance)) {
    showComingSoon = true;
  }

  const defaultSelected = leaseOptions.find(x => x.downPayment === defaultDownPayment);

  return (
    <Box>
      {
        showLease &&
        <>
          <Row mb='26px'>
            <MonthlyPaymentText fontSize='15px' lineHeight='26px'>Est. Lease Payment</MonthlyPaymentText>
            <MonthlyPaymentText fontSize='20px' lineHeight='28px'>{defaultSelected?.monthlyFormatted}/mo</MonthlyPaymentText>
          </Row>
          <InfoTitleText>{defaultSelected?.downPaymentFormatted} down, {termsFormatted}, {annualMiles}</InfoTitleText>
          <InfoText>Includes {destinationFeeFormatted} destination fee</InfoText>
          <InfoText>Excludes taxes & other fees</InfoText>
          <InfoText>Available in select states</InfoText>
          <InfoText>Subject to credit approval</InfoText>
          <FlexCol mt='16px' alignItems='center'>
            <MoreOptionButton onClick={onMoreClick}>{`More Lease options >`}</MoreOptionButton>
          </FlexCol>
        </>
      }
      {
        showComingSoon &&
        <>
          <ComingSoonText>Leasing program is coming soon.</ComingSoonText>
          <ContactText>For more information, please contact <PhoneNumber>(800) 228-7702</PhoneNumber></ContactText>
        </>
      }
    </Box>
  );
}

function CashPrice({ subtotal }) {
  return (
    <>
      <Row my='15px'>
        <P16>Vehicle Price</P16>
        <P16><Currency>{subtotal}</Currency></P16>
      </Row>
      <Row my='15px'>
        <P12 color={colors.content1}>Excludes sales tax, delivery fee, registration fee, DMV fee, etc.</P12>
      </Row>
    </>
  );
}

const PhoneNumber = styled.span({
  whiteSpace: 'nowrap',
});

const MonthlyPaymentText = styled(Text)({
  color: 'rgba(0, 0, 0, 1)'
});

const InfoTitleText = styled(Text)({
  fontSize: '14px',
  lineHeight: '22px',
  color: 'rgba(23, 26, 32, 1)'
});

const InfoText = styled(Text)({
  fontSize: '12px',
  lineHeight: '20px',
  color: 'rgba(92, 94, 98, 1)'
});

const ComingSoonText = styled(Text)({
  fontSize: '14px',
  lineHeight: '22px',
});

const ContactText = styled(Text)({
  fontSize: '12px',
  lineHeight: '20px',
  marginTop: '1px',
  color: 'rgba(92, 94, 98, 1)'
});

const MoreOptionButton = styled(Button)({
  '& .ff-ui-button-label': {
    fontSize: '14px',
    lineHeight: '24px',
    textTransform: 'none',
    fontWeight: 400,
    color: 'rgba(23, 26, 32, 1)',
    '&:hover': {
      textDecoration: 'underline',
    }
  }
});

const Divider = styled(Box)({
  borderTop: colors.border2,
});

const Divider2 = styled(Box)({
  borderTop: colors.border3,
});

const Row = styled(FlexRow)({
  alignItems: 'center',
  justifyContent: 'space-between'
});
